import { useEffect, useRef } from 'react';
import {
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    LogLevel
} from '@microsoft/signalr';
import { AuthHelper } from '../utilities/helpers/AuthHelper';

const HUB_PATH = '/hub/subscriptions';
const INITIAL_RETRY_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];
const POST_CONNECT_RETRY_DELAYS_MS = [0, 2_000, 5_000, 10_000, 30_000];

export type SubscriptionChangedEvent = {
    subscriptionId: number;
    tenantId: number;
    kind: 'Created' | 'Updated' | 'Canceled' | 'Reconciled';
    occurredAt: string;
};

export type StripeCatalogChangedEvent = {
    occurredAt: string;
};

export type ConnectionState = 'Connecting' | 'Connected' | 'Reconnecting' | 'Disconnected';

type Options = {
    tenantIds: number[];
    joinAdminGlobal: boolean;
    onChanged: (event: SubscriptionChangedEvent) => void;
    /** Optional. Fires when the Stripe-product catalog mutated (CRUD or sync apply).
     *  Only delivered when joinAdminGlobal=true (server broadcasts to AdminGlobal only).
     *  Recipients refetch the full catalog via GET — the event carries no diff. */
    onCatalogChanged?: (event: StripeCatalogChangedEvent) => void;
    /** Fires after every successful (initial OR re-) join. Wire your list-refetch here.
     *  This guarantees subscribe-then-fetch ordering: by the time onReady fires, the client
     *  is in the joined groups and any event that arrived before this point would have been
     *  bypassed — the consumer's refetch captures whatever state the server holds NOW. */
    onReady?: () => void;
    /** Optional UX hook: fires whenever the underlying connection state changes. Useful for
     *  showing a "live updates unavailable" banner when state stays Disconnected for > N seconds. */
    onConnectionStateChange?: (state: ConnectionState) => void;
};

// REACT_APP_API_URL points at the controller path prefix (e.g. https://host/api/portal/),
// but SignalR hubs are mapped at the server root (see Startup.cs MapHub). Use the origin
// only so we don't end up POSTing /api/portal/hub/subscriptions/negotiate → 404.
const resolveHubBaseUrl = (): string => {
    const raw = process.env.REACT_APP_API_URL ?? '';
    try { return new URL(raw).origin; } catch { return raw.replace(/\/+$/, ''); }
};

const buildConnection = (): HubConnection => {
    const baseUrl = resolveHubBaseUrl();
    return new HubConnectionBuilder()
        .withUrl(`${baseUrl}${HUB_PATH}`, {
            accessTokenFactory: async () => (await AuthHelper.getDocubirdToken()) ?? '',
            // Auth is JWT-only (Authorization header for REST, ?access_token= query for WS).
            // Disabling credentials lets the server keep its wildcard CORS policy without
            // tripping the spec rule that forbids Access-Control-Allow-Origin: * on
            // credentialed requests. See Startup.cs UseCors().
            withCredentials: false
        })
        .withAutomaticReconnect(POST_CONNECT_RETRY_DELAYS_MS)
        .configureLogging(LogLevel.Warning)
        .build();
};

const arraysShallowEqual = (a: number[], b: number[]) =>
    a.length === b.length && a.every((v, i) => v === b[i]);

/**
 * Opens a SignalR connection to /hub/subscriptions for as long as the consumer is
 * mounted, joins view-scoped groups, and invokes onChanged for every server push.
 *
 * Lifecycle:
 *   - mount: start (with backoff retry on failure) -> JoinAsync -> onReady
 *   - scope change: LeaveAsync(old) + JoinAsync(new) -> onReady (no stop)
 *   - reconnect: JoinAsync re-issued -> onReady (consumer should re-fetch the list)
 *   - unmount: LeaveAsync + stop, immediately (no linger)
 *
 * The hook reads the latest scope from `lastScopeRef.current` after `start()` resolves,
 * so a scope change during the connection-establishment window is honoured by the
 * initial JoinAsync (not the mount-time captured value).
 *
 * This hook is end-to-end verified via the smoke tests in
 * `docs/superpowers/plans/2026-05-25-realtime-subscription-updates.md` Task 16 (multi-tab,
 * marketplace, network-interruption, presence-leak). Unit-testing the SignalR client
 * lifecycle in jsdom requires reimplementing the library's chain builder and adds little
 * value beyond verifying the mock — the smoke tests directly exercise the real transport.
 */
export const useSubscriptionsHubConnection = ({
    tenantIds, joinAdminGlobal, onChanged, onCatalogChanged, onReady, onConnectionStateChange
}: Options) => {
    const connectionRef = useRef<HubConnection | null>(null);
    const lastScopeRef = useRef<{ tenantIds: number[]; joinAdminGlobal: boolean }>({
        tenantIds: [...tenantIds], joinAdminGlobal
    });
    // Tracks the scope that the server actually accepted (last successful JoinAsync).
    // Compared by the scope-change effect to compute the Leave/Join delta. Initialized
    // by the mount effect after the initial JoinAsync succeeds.
    const lastJoinedScopeRef = useRef<{ tenantIds: number[]; joinAdminGlobal: boolean } | null>(null);
    const onChangedRef = useRef(onChanged);
    const onCatalogChangedRef = useRef(onCatalogChanged);
    const onReadyRef = useRef(onReady);
    const onStateChangeRef = useRef(onConnectionStateChange);

    // Keep latest callbacks reachable without re-binding the SignalR handlers.
    useEffect(() => { onChangedRef.current = onChanged; }, [onChanged]);
    useEffect(() => { onCatalogChangedRef.current = onCatalogChanged; }, [onCatalogChanged]);
    useEffect(() => { onReadyRef.current = onReady; }, [onReady]);
    useEffect(() => { onStateChangeRef.current = onConnectionStateChange; }, [onConnectionStateChange]);

    // Keep lastScopeRef in sync on EVERY render where the scope changes — even when the
    // connection isn't connected yet. The mount effect reads this after start() resolves,
    // so a fast scope change during connection-establishment is honoured.
    useEffect(() => {
        lastScopeRef.current = { tenantIds: [...tenantIds], joinAdminGlobal };
    });

    // Mount: build, start (with retry), JoinAsync, fire onReady. Unmount: leave + stop.
    useEffect(() => {
        let cancelled = false;
        let retryTimer: ReturnType<typeof setTimeout> | null = null;
        let reconnectInFlight = false;
        const connection = buildConnection();
        connectionRef.current = connection;

        connection.on('SubscriptionChanged', (event: SubscriptionChangedEvent) => {
            onChangedRef.current?.(event);
        });

        // Catalog-changed is broadcast to AdminGlobal only; non-admins join no admin group
        // so they never receive these. Safe to wire unconditionally on the client.
        connection.on('StripeCatalogChanged', (event: StripeCatalogChangedEvent) => {
            onCatalogChangedRef.current?.(event);
        });

        const joinNow = async () => {
            // Read scope NOW (latest, not the one captured at last successful join) so a
            // scope change during the connect/reconnect window is honoured.
            const scope = lastScopeRef.current;
            try {
                await connection.invoke('JoinAsync', {
                    TenantIds: scope.tenantIds,
                    JoinAdminGlobal: scope.joinAdminGlobal
                });
                lastJoinedScopeRef.current = { ...scope, tenantIds: [...scope.tenantIds] };
                onReadyRef.current?.();
            } catch {
                // Join failed but connection is live — next scope-change or reconnect re-joins.
            }
        };

        const startWithRetry = async (attempt: number = 0): Promise<boolean> => {
            if (cancelled) return false;
            onStateChangeRef.current?.('Connecting');
            try {
                await connection.start();
                return true;
            } catch {
                if (cancelled) return false;
                const delay = INITIAL_RETRY_DELAYS_MS[Math.min(attempt, INITIAL_RETRY_DELAYS_MS.length - 1)];
                onStateChangeRef.current?.('Disconnected');
                await new Promise<void>(resolve => {
                    retryTimer = setTimeout(() => { retryTimer = null; resolve(); }, delay);
                });
                return startWithRetry(attempt + 1);
            }
        };

        const startAndJoin = async () => {
            // Dedupe against parallel reconnect attempts (e.g. onclose firing while a
            // retry is already in flight). Without this, two startWithRetry loops would
            // race and we'd issue duplicate JoinAsync calls.
            if (reconnectInFlight) return;
            reconnectInFlight = true;
            try {
                const started = await startWithRetry();
                if (!started || cancelled) return;
                onStateChangeRef.current?.('Connected');
                await joinNow();
            } finally {
                reconnectInFlight = false;
            }
        };

        connection.onreconnecting(() => {
            onStateChangeRef.current?.('Reconnecting');
        });

        connection.onreconnected(async () => {
            onStateChangeRef.current?.('Connected');
            // Server may have evicted memberships during the gap. Re-issue join, then
            // fire onReady so the consumer can do a full list refetch (recovering any
            // pushes that arrived during the disconnect).
            await joinNow();
        });

        connection.onclose(() => {
            onStateChangeRef.current?.('Disconnected');
            // withAutomaticReconnect has given up. Reset our own retry loop so the
            // connection eventually self-heals after long outages (>47s for the default
            // delay schedule). Without this restart, the list goes silently stale until
            // the user manually refreshes the page.
            if (!cancelled) {
                // Intentionally not awaited — the IIFE inside startAndJoin runs its own
                // retry loop; awaiting here would block the SignalR callback queue.
                startAndJoin();
            }
        });

        startAndJoin();

        return () => {
            cancelled = true;
            if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
            (async () => {
                try {
                    if (connection.state === HubConnectionState.Connected) {
                        const scope = lastScopeRef.current;
                        await connection.invoke('LeaveAsync', {
                            TenantIds: scope.tenantIds,
                            LeaveAdminGlobal: scope.joinAdminGlobal
                        });
                    }
                    await connection.stop();
                } catch { /* swallow shutdown errors */ }
                connectionRef.current = null;
            })();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Scope change: leave old, join new — keep the connection alive. Fire onReady after.
    // Uses lastJoinedScopeRef (the scope the server actually accepted) to compute the delta,
    // since lastScopeRef is updated optimistically every render.
    useEffect(() => {
        const conn = connectionRef.current;
        if (!conn) return;
        const prev = lastJoinedScopeRef.current;
        if (prev && arraysShallowEqual(prev.tenantIds, tenantIds) && prev.joinAdminGlobal === joinAdminGlobal) {
            return; // no actual scope change
        }
        if (conn.state !== HubConnectionState.Connected) {
            // Connection not ready — the initial mount effect will use the latest scope (read
            // from lastScopeRef) once start() resolves. Don't try to invoke now.
            return;
        }
        const newScope = { tenantIds: [...tenantIds], joinAdminGlobal };
        (async () => {
            try {
                if (prev) {
                    await conn.invoke('LeaveAsync', {
                        TenantIds: prev.tenantIds,
                        LeaveAdminGlobal: prev.joinAdminGlobal
                    });
                }
                await conn.invoke('JoinAsync', {
                    TenantIds: newScope.tenantIds,
                    JoinAdminGlobal: newScope.joinAdminGlobal
                });
                lastJoinedScopeRef.current = newScope;
                onReadyRef.current?.();
            } catch { /* next event / reconnect will re-establish */ }
        })();
    }, [tenantIds, joinAdminGlobal]);
};
